// Read-only, nesting-preserving render of a FilterGroup. Reused by the
// AnaMoni summary, the active-filter chips, and the advanced builder so a
// compound query reads the same everywhere. Never mutates state.
import type { ReactNode } from "react";
import type { FilterGroup } from "../types/inpact";
import { describeFilterTree, type DescribedNode } from "../lib/describeFilterTree";

export function FilterTreePreview({
  group,
  catalogueLabels,
}: {
  group: FilterGroup;
  /** field -> label from the live catalogue (`useFields()` + `labelsFromFields`);
   *  omit to fall back to the static label list (see `filterLogic.fieldLabel`). */
  catalogueLabels?: Record<string, string>;
}) {
  const root = describeFilterTree(group, catalogueLabels);
  if (root.kind === "group" && root.children.length === 0) {
    return (
      <p className="filter-preview filter-preview--empty" data-testid="filter-preview">
        No filters — showing everyone.
      </p>
    );
  }
  return (
    <div className="filter-preview" data-testid="filter-preview">
      <NodeView node={root} top />
    </div>
  );
}

// Dispatches on node shape. Kept tiny; each branch's rendering lives in its
// own single-responsibility component below (30-line function budget).
function NodeView({ node, top = false }: { node: DescribedNode; top?: boolean }) {
  if (node.kind === "rule") {
    return <RuleView text={node.text} />;
  }
  if (node.logic === "NOT") {
    return <NotGroupView child={node.children[0]} />;
  }
  return <GroupView node={node} top={top} />;
}

function RuleView({ text }: { text: string }) {
  return <span className="filter-preview__rule">{text}</span>;
}

// The NOT's operand may be absent from a malformed tree — guard before
// dereferencing. A group operand is parenthesized the same way GroupRow
// parenthesizes a nested group (so `NOT (A AND B)` reads unambiguously); a
// bare rule operand needs no parens.
function NotGroupView({ child }: { child: DescribedNode | undefined }) {
  if (!child) return null;
  return (
    <span className="filter-preview__group" data-logic="NOT">
      <span className="filter-preview__not">Does not match:</span>{" "}
      {child.kind === "group" ? (
        <Parenthesized><NodeView node={child} /></Parenthesized>
      ) : (
        <NodeView node={child} />
      )}
    </span>
  );
}

type GroupNode = Extract<DescribedNode, { kind: "group" }>;

function GroupView({ node, top }: { node: GroupNode; top: boolean }) {
  const connector = node.logic === "OR" ? "OR" : "AND";
  const className = top
    ? "filter-preview__group"
    : "filter-preview__group filter-preview__group--nested";
  return (
    <div className={className} data-logic={node.logic}>
      {node.children.map((child, i) => (
        <GroupRow key={i} child={child} connector={connector} showConnector={i > 0} />
      ))}
    </div>
  );
}

function GroupRow({ child, connector, showConnector }: {
  child: DescribedNode;
  connector: string;
  showConnector: boolean;
}) {
  return (
    <div className="filter-preview__row">
      {showConnector && (
        <span className="filter-preview__connector" data-testid="fp-connector">
          {connector}
        </span>
      )}
      {child.kind === "group" ? (
        <Parenthesized><NodeView node={child} /></Parenthesized>
      ) : (
        <NodeView node={child} />
      )}
    </div>
  );
}

// Shared `( ... )` wrapper for a nested-group operand, used by both a
// GroupRow's group child and a NOT's group operand so both read identically.
function Parenthesized({ children }: { children: ReactNode }) {
  return (
    <span className="filter-preview__paren">
      <span aria-hidden>(</span>
      {children}
      <span aria-hidden>)</span>
    </span>
  );
}
